// src/components/Facilities/clutchRowHelpers.js
//
// I-03 R5 · S3-05.2 — the Clutches grid's row derivations, at MODULE
// SCOPE, and the copy its confirms and refusals read from.
//
// The same shape `animalRowHelpers.js` took in S3-05.1: every derivation
// that used to be an inline IIFE or an object literal inside a table cell
// lives here, so `ClutchRow` can be one function and the sentences can be
// asserted by name.
//
// ⚠️ THREE THINGS ARE IMPORTED FROM `animalRowHelpers`, NOT COPIED.
// `getAnimalCommonName` and `scientificName` read ONLY `.common_name` and
// `.species`, which a clutch row carries identically (the same
// `NewSpeciesImageSerializer` nested under the same key) — so a second
// copy here would be a second answer to "what is this species called".
// `UNLISTS_THE_ANIMAL` / `UNLISTS_TONE` are the same argument about a
// CONSEQUENCE: taking a row off sale is one act with one wording and one
// tone, and the fix-pass-4 comment on those constants exists because four
// popovers once described it in two registers. Their names predate
// clutches; the strings do not mention animals.
// ⚠️ AND A FOURTH, from Cowork run 22 (finding 10): `NO_PLACE` — the em
// dash a Where cell reads when the row is in no tank. This grid printed
// the WORD "None" for the state the Animals grid printed an em dash for,
// on two pages an operator reads one after the other, and "None" reads
// like a tank called None. One constant, both grids.
import {
  getAnimalCommonName, scientificName, UNLISTS_THE_ANIMAL, UNLISTS_TONE,
  NO_PLACE, placeLines,
} from './animalRowHelpers';
// R2 naming — the ONE implementation of "rack name, then slot".
import { rackAndSlot } from '../Racks/naming';

export { getAnimalCommonName, scientificName, NO_PLACE };

/**
 * The Where cell — "Dart 1 — Rack 2 · 1.3". CARRIED ACROSS BYTE FOR BYTE
 * from `ClutchGrid`, including its comment, because
 * `ClutchGridLocationCell.test.js` asserts this cell's text EXACTLY.
 *
 * **THE RACK GOES BESIDE THE SLOT** (operator ruling 2026-09-14). A
 * tank's position LABEL is the slot WITHIN a rack, and every rack in the
 * building has a 1.3 — so this list, which shows a tank OUTSIDE its rack,
 * has to name the rack next to it. `rackAndSlot` is the ONE
 * implementation of that pairing.
 *
 * ⚠️ **THE SLOT IS NEVER PRINTED WITHOUT ITS RACK.** `clutch.enclosure`
 * is the full `EnclosureSerializer`, which carries the SLOT
 * (`position_label`) and `rack_name`. The guard stays anyway: with no
 * rack name on the payload this prints the tank's own name ALONE rather
 * than "1.3", which reads like an answer and points at eight different
 * tanks.
 */
export const enclosureCellText = (enclosure) => {
  const rackName = enclosure?.rack_name || '';
  const place = rackName
    ? (rackAndSlot({ rackName, positionLabel: enclosure.position_label })
      || rackName)
    : '';
  // The em dash separates the tank from WHERE it is; the ` · ` inside
  // the pair belongs to the rack/slot pairing alone.
  return `${enclosure.name}${place ? ` — ${place}` : ''}`;
};

/** The Where cell's two lines — `animalRowHelpers.placeLines`, the ONE
 *  rule, read off the clutch's nested tank. */
export const enclosureCellLines = (enclosure) => placeLines(
  enclosure?.name, enclosure?.rack_name, enclosure?.position_label,
);

/**
 * AN EGG CUP ON A RACK SHELF (Cowork QA 2026-10-28). A cup placed straight
 * on a shelf is in no tank, so `clutch.enclosure` is null and this cell
 * read "—" for a clutch that is plainly somewhere. The row's `shelf_place`
 * (server-side: the rack, and the slot spelled by the ONE rule a tank's
 * `position_label` uses) goes through `placeLines` exactly as a tank does
 * — "Egg cup — Incubator 1 · 2.3" — so the rack is always beside the slot.
 *
 * ⚠️ A shelf always has a rack; a payload with no rack name prints the
 * first line alone, never "freestanding" (a cup on a shelf is not
 * freestanding) and never a bare slot.
 */
export const CUP_ON_SHELF = 'Egg cup';

export const cupCellLines = (shelfPlace) => {
  const rack = String(shelfPlace?.rack_name || '').trim();
  if (!rack) return { tank: CUP_ON_SHELF, place: '', rack: '', slot: '' };
  return placeLines(CUP_ON_SHELF, rack, shelfPlace.position_label);
};

/** "this clutch" when it has no name — every sentence below reads it. */
export const who = (name) => name || 'this clutch';

/**
 * THE STATUS CHIPS, IN THE OPERATOR'S ORDER — possession first, then the
 * commercial states, then the two that end a record. Fixed on purpose:
 * an operator scanning a page reads the same position for the same fact
 * every time, which is the whole reason a row of chips beats six columns
 * of YES/NO badges.
 *
 * ⚠️ **ONLY TWO OF THEM ARE WRITABLE** (review round 1, the Bypass
 * Lockdown ruling applied to clutches). The first cut made Sold, On hold
 * and For sale one click each, and the old clutch modal offered none of
 * the three:
 *
 *   · a hand-set **Sold** carries no `Order` and no `OrderReservation` —
 *     the GROUP-level orphan that policy exists to prevent (~38 cleaned
 *     off prod on 2026-05-12) — and a hand-set **On hold** is a hold that
 *     expires never, because nothing is behind it to expire;
 *   · **For sale** is derived from the listing the clutch is attached to,
 *     and nothing in the app has ever written it on a group;
 *   · **Split** is the Split action's, and a clutch un-split by hand
 *     claims back animals that have their own rows now.
 *
 * `writable: false` means the chip is a `StateChip` — a span, with the
 * reason in its `title`. NOT `aria-disabled`, which says "a control,
 * currently unavailable": these are not controls at all.
 *
 * ⚠️ **AND THE THREE COMMERCIAL ONES ARE DRAWN ONLY WHEN THEY ARE ON**
 * (operator ruling, 2026-09-26: *"I don't want to sell clutches."*).
 * `legacyOnly: true` means the chip appears for a row whose flag is set and
 * for no other row — so a clutch reads `In possession · Deceased · Split`
 * and nothing about a sale, which is every clutch the operator will ever
 * make. The three are still in the table, and still in this order, because
 * a LEGACY row carries them: a `is_sold` written before the ruling (or by
 * the Django admin, the only door that ever reached one) is a fact about
 * that row and hiding it outright would be the grid lying about its own
 * data. What they may not do is offer three permanently-off words, in a
 * commercial vocabulary, on every row of a surface where none of it is
 * reachable — which is how the operator read them as something to press.
 */
export const STATUS_FLAGS = [
  { field: 'is_in_possession', label: 'In possession', tone: 'yes', writable: true },
  { field: 'is_for_sale', label: 'For sale', tone: 'yes', legacyOnly: true },
  { field: 'is_on_hold', label: 'On hold', tone: 'yes', legacyOnly: true },
  { field: 'is_sold', label: 'Sold', tone: 'sold', legacyOnly: true },
  { field: 'is_deceased', label: 'Deceased', tone: 'deceased', writable: true },
];

/**
 * WHY A COMMERCIAL CHIP IS ON A CLUTCH AT ALL, said in one sentence — the
 * title of all three (operator ruling, 2026-09-26).
 *
 * ⚠️ IT NAMES NO DOOR, AND THAT IS THE WHOLE POINT. The three titles used
 * to name one each: Sold and On hold said *"Set by an order — a historical
 * sale goes through the Manual Order wizard"* and For sale said *"Set by
 * Manage Products…"* (fix pass 2 had already cut "Manage Products" out of
 * that one for naming a door a clutch does not have). Both were answers to
 * the wrong question. The operator does not sell clutches — they sell
 * ANIMALS, and a clutch is a tub of eggs — so the honest reading of a set
 * flag is not "another screen owns this" but "this row predates the
 * ruling". Copy naming a control the operator can go and fail to find is
 * run 4's C7/D3 finding, and the Manual Order wizard was worse than
 * unreachable: it was an invitation to do the thing the ruling forbids.
 *
 * The SERVER's own refusal for a hand-set `is_for_sale`
 * (`serializers.CLUTCH_FOR_SALE_SET_ELSEWHERE`) is unchanged and is pinned
 * in `test_clutch_inline_update.ForSaleIsNotTheClientsEitherTests`; it is
 * no longer mirrored here, because a refusal nothing on this grid can
 * provoke is the server's sentence to keep, not the grid's to copy.
 */
export const NOT_SOLD_HERE = 'A legacy flag — clutches are not sold here.';

/**
 * WHY A CHIP IS READ-ONLY: the door that sets it, named so the operator can
 * go there — or, where there is none, what the state actually means. A
 * state that only says "not here" is the dead end run 9's R2-2 ruling is
 * about, and `Split` has a real door to name.
 *
 * The three commercial ones share `NOT_SOLD_HERE`, because they share one
 * explanation: the row is old. They are only ever drawn when they are on.
 *
 * `Split` is not in `STATUS_FLAGS` (it is drawn after the divider) and is
 * keyed here with the rest, because it is the same kind of fact.
 */
export const SET_ELSEWHERE = {
  is_sold: NOT_SOLD_HERE,
  is_on_hold: NOT_SOLD_HERE,
  is_for_sale: NOT_SOLD_HERE,
  is_split: 'Set by the Split action',
};

/**
 * A count of 0 makes a counted clutch INACTIVE by the occupancy rule
 * (`archiving.OCCUPYING_GROUP`) and `list_clutches` HIDES count-0 rows
 * unless `include_empty` is on — so the commonest destructive-looking
 * edit on this grid is one that makes the row disappear on the next
 * refetch. It asks, and it names the control that brings it back.
 *
 * ⚠️ THE CONTROL IS NAMED AS IT IS SPELLED IN THE FILTER BAR, and the
 * `<select>` RENDERS THIS CONSTANT — no operator-facing copy may name a
 * control that does not exist, or name it in words that are not on it
 * (run 4, C7/D3), and two spellings of one option is how that happens. It
 * is a dropdown, so the verb is "choose".
 *
 * Read by `zeroCountConfirm`, by the Deceased confirm (which hides the row
 * the same way) and by `ClutchGrid`'s own filter bar.
 * ⚠️ **THE DASH IS AN EM DASH** (Cowork run 22, finding 10). The
 * racks page's own copy (`Racks/rackHelpers.cupLeftover`) named this
 * option with an em dash and this constant spelled it with a hyphen, so
 * the sentence that tells the operator where a leftover cup went named a
 * control spelled differently from the one they then read in the filter
 * bar. `rackHelpers` READS this constant now — one spelling, every
 * surface that names the option.
 */
export const EMPTY_SHOWN_OPTION = 'Empty/split \u2014 Shown';

/** Its twin, so the two options in the same `<select>` match. */
export const EMPTY_HIDDEN_OPTION = 'Empty/split \u2014 Hidden';

/**
 * The Deceased filter's "All" option, AS THE FILTER BAR PRINTS IT (Cowork
 * run 23, finding 7). A DECEASED clutch is hidden by the Deceased filter's
 * default ("No"), not by the Empty/split one — the confirm used to send
 * the operator to "Empty/split — Shown", which does not bring a deceased
 * clutch back. The `<select>` renders this constant, like the Empty/split
 * pair above, so the sentence and the control cannot drift.
 */
export const DECEASED_ALL_OPTION = 'Deceased - All';

/**
 * The popover a WRITABLE clutch flag needs before it is changed.
 *
 * ⚠️ TWO FLAGS ARE WRITABLE AND EACH ASKS IN ONE DIRECTION ONLY.
 *
 *   · **Deceased ON** always asks: it changes what the racks page and the
 *     archive believe about the clutch's tank (the occupancy rule,
 *     operator rulings 2026-09-08, `services/archiving.py`) AND it hides
 *     the row behind the list's default filter, which the sentence names
 *     the escape from — the same shape `zeroCountConfirm` uses, for the
 *     same reason.
 *   · **In possession OFF** asks only when the clutch is ON the
 *     storefront: that is when it has a consequence there, and a confirm
 *     for a consequence that cannot happen is a click the operator learns
 *     to dismiss without reading.
 *
 * `onStorefront` is the SERVER's answer (`is_on_storefront`: the clutch is
 * linked to a variation that is actually for sale), never the group's own
 * `is_for_sale` — a group flagged for sale but attached to nothing sells
 * nowhere, and telling the operator it leaves a storefront it was never on
 * is the wrong sentence.
 *
 * Turning Deceased OFF, or In possession ON, restores a state and asks
 * nothing.
 */
export const turnOnConfirm = (field, name, { onStorefront = false } = {}) => {
  const subject = who(name);
  if (field === 'is_deceased') {
    return {
      title: `Mark ${subject} deceased?`,
      body: `A deceased clutch no longer counts as living in its tank. `
        + 'It will be hidden by the default filter and leaves the default list; '
        + `choose "${DECEASED_ALL_OPTION}" in the Deceased filter to see it.`
        + (onStorefront ? ` ${UNLISTS_THE_ANIMAL}` : ''),
      confirmLabel: 'Mark deceased',
      tone: 'danger',
    };
  }
  return null;
};

/**
 * ...and the one a WRITABLE flag needs before it is turned OFF. Only In
 * possession has one, and only on the storefront.
 */
export const turnOffConfirm = (field, name, { onStorefront = false } = {}) => {
  if (field === 'is_in_possession' && onStorefront) {
    return {
      title: `Mark ${who(name)} no longer in possession?`,
      body: UNLISTS_THE_ANIMAL,
      confirmLabel: 'Not in possession',
      tone: UNLISTS_TONE,
    };
  }
  return null;
};

export const zeroCountConfirm = (name) => ({
  title: `Set ${who(name)} to 0?`,
  body: `It will be hidden by the default filter; choose "${EMPTY_SHOWN_OPTION}" to see it.`,
  confirmLabel: 'Set to 0',
  tone: UNLISTS_TONE,
});

/**
 * THE SERVER'S OWN SENTENCE, whichever door answered.
 *
 * ⚠️ THE CLUTCH ENDPOINT HAS THREE REFUSAL SHAPES and the operator reads
 * all three: `{"errors": {"non_field_errors": [...]}}` (the `is_valid()`
 * branch and, since S3-05.2, the `ValidationError` branch — both through
 * `views.clutch_field_refusal` / `clutch_save_refusal`), `{"detail": …}`
 * (the facility/species/enclosure checks and the for-sale refusal) and
 * `{"message": …}`. A reader that knew only one of them printed a
 * generic line over a sentence the server had written for a human, which
 * is the run-3 "the failure message was an instruction to retry" bug.
 *
 * NEVER a column name and never a code: `errors` is read for
 * `non_field_errors` only, because that is where both mappings put their
 * sentences.
 */
export const clutchSentence = (err, fallback = 'Could not save that.') => {
  const data = err?.response?.data;
  if (!data) return err?.message || fallback;
  if (typeof data === 'string') return data;
  const nonField = data.errors && data.errors.non_field_errors;
  if (Array.isArray(nonField) && nonField.length) return nonField.join(' ');
  if (typeof nonField === 'string' && nonField) return nonField;
  if (Array.isArray(data.non_field_errors) && data.non_field_errors.length) {
    return data.non_field_errors.join(' ');
  }
  return data.message || data.error || data.detail || fallback;
};

/**
 * `AnimalGroup.birth_date` is NOT NULL, so clearing the lay date is not
 * an edit the model can take. THE SERVER'S OWN SENTENCE
 * (`views.CLUTCH_REQUIRED_SENTENCES['birth_date']`) is the one said here,
 * so the client's refusal and the server's cannot differ — the same
 * belt-and-braces shape `InlineDate`'s year guard uses, and pinned on
 * both sides.
 */
export const LAY_DATE_REQUIRED = 'A clutch needs a lay date.';

/**
 * Why Delete is unavailable, or nothing. CARRIED ACROSS VERBATIM from
 * `EditClutchModal` — every sentence is pinned by `DeleteClutch.test.js`
 * and the backend refuses on the same four states
 * (`archiving.clutch_delete_blocker`: `group_not_empty`, `group_placed`,
 * `group_listed`).
 *
 * Every reason reads the SERVER's state off the row, never an unsaved
 * form: the count in the database is what DELETE will look at.
 */
export const deleteBlockedReason = (clutch) => {
  const rawCount = clutch?.current_count;
  const countKnown = rawCount !== null && rawCount !== undefined
    && rawCount !== '' && Number.isFinite(Number(rawCount));
  const serverCount = countKnown ? Number(rawCount) : null;
  const isPlaced = Boolean(
    clutch?.shelf || clutch?.enclosure?.id || clutch?.enclosures_id,
  );
  if (!countKnown) {
    // `|| 0` here would have read a MISSING count as an empty clutch and
    // offered to delete it. Unknown is not zero.
    return 'Count unknown for this clutch — reopen it from the Clutches page before deleting.';
  }
  if (serverCount > 0) {
    return `Still holds ${serverCount} ${serverCount === 1 ? 'animal' : 'animals'} — split it, move them out, or save a count of 0 first.`;
  }
  if (isPlaced) {
    return clutch?.enclosure?.name
      ? `Still placed in ${clutch.enclosure.name} — take it out first.`
      : 'Still placed on a rack shelf — take it off first.';
  }
  if (clutch?.is_listed) {
    // ProductAnimal.animal_group is CASCADE — deleting would take a live
    // listing dark with no warning. The backend refuses with
    // `group_listed`; this says it first.
    return 'Still linked to a product listing — unlink it from the product first.';
  }
  return null;
};

// ── sorting (§5) ──────────────────────────────────────────────────────
//
// CLIENT-SIDE OVER THE WHOLE LIST, because `list_clutches` returns a FLAT
// ARRAY unless pagination is asked for — `wants_pagination` is true only
// for `paginate=true` / `page` / `page_size` / `itemsPerPage`, and
// `getClutchesByFacility` sends none of them (the grid's paging is its
// own, client-side, like the animals grid's). So sorting sorts every row
// the operator can reach, and the header says nothing about pages.
//
// The comparator is `animalRowHelpers`' rule, for its reason: a key
// answers `[isMissing, value]` — THE FLAG FIRST — and the flag is ranked
// WITHOUT the direction sign, so a row with nothing to sort by loses in
// BOTH directions. A sentinel value cannot do that: it works ascending
// and inverts descending, which is the same bug wearing a sign.
/** "There is nothing to sort by here." The value is never compared. */
const MISSING = [1, null];
const present = (value) => [0, value];

const textOrMissing = (value) => {
  const s = value == null ? '' : String(value).trim().toLowerCase();
  return s ? present(s) : MISSING;
};

const numberOrMissing = (value) => {
  if (value == null || value === '') return MISSING;
  const n = Number.parseFloat(value);
  return Number.isNaN(n) ? MISSING : present(n);
};

export const SORT_KEYS = {
  name: (c) => textOrMissing(c.name),
  species: (c) => textOrMissing(scientificName(c)),
  // By `age_days`, never by the DISPLAY string: "14 mo" and "9 wk" do not
  // compare as text.
  age: (c) => numberOrMissing(c.age_days),
};

/**
 * A new list, sorted. `key: null` means "the server's own order"
 * (`-birth_date, id`), which is the default: until a header is clicked the
 * grid shows exactly what the endpoint sent.
 *
 * The tiebreak is the id, so equal values keep ONE order across renders.
 */
export const sortClutches = (clutches, key, dir) => {
  const read = key && SORT_KEYS[key];
  if (!read) return clutches;
  const sign = dir === 'desc' ? -1 : 1;
  return [...clutches].sort((a, b) => {
    const [aMissing, av] = read(a);
    const [bMissing, bv] = read(b);
    if (aMissing !== bMissing) return aMissing - bMissing;
    if (!aMissing) {
      if (av < bv) return -1 * sign;
      if (av > bv) return 1 * sign;
    }
    return String(a.id).localeCompare(String(b.id));
  });
};
